import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, Typography } from '@mui/material';
import { KeyboardEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BaseConfirmDrawer from '../../ui/BaseConfirmDrawer';
import CaptureManager from '@/mobile/components/CaptureManager';
import ScannerInputField, { ScannerInputFieldRef } from '@/mobile/components/ScannerInputField';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { COOL_DOWN_MS, DUPLICATE_TTL_MS, validateEslMac } from '@/mobile/utils/mobileUtils';
import { decodeMac, encodedMACFormat } from '@/utils/inputUtils';
import useSoundPlayer from '@/mobile/hooks/useSoundPlayer';
import { addToWhiteListAPI, registerTagAPI } from '@/api/storeManagement/registration';
import { useAppContext } from '@/provider/contexts';
import BaseDialog from '@/components/ui/BaseDialog';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';
import { useMobileState } from '@/mobile/provider/context';

type PendingMac = {
    mac: string;
};
const normalizeEslMac = (raw: string) => {
    return encodedMACFormat(raw) ? decodeMac(raw.substring(2)) : raw;
};

const AddTagDrawer = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { refreshTable } = useRegistrationLifecycleState();
    const { dispatch: mobileDispatch } = useMobileState();
    const { state, dispatch } = useRegistrationState();
    const { playScan } = useSoundPlayer();

    const [mac, setMac] = useState('');
    const [pendingMacs, setPendingMacs] = useState<PendingMac[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [openConfirmedTags, setOpenConfirmedTags] = useState<boolean>(false);
    const [confirmedTags, setConfirmedTags] = useState<string[]>([]);
    const macRef = useRef<ScannerInputFieldRef | null>(null);
    const scanGateRef = useRef({
        locked: false,
        lastValue: '',
        lastAt: 0,
    });

    const pendingMacSet = useMemo(() => {
        return new Set(pendingMacs.map((item) => item.mac));
    }, [pendingMacs]);

    const closeDrawer = useCallback(() => {
        dispatch({
            type: 'TOGGLE_DIALOG',
            payload: {
                dialog: 'isTagOpen',
                isOpen: false,
            },
        });
    }, []);

    const clearDrawer = useCallback(() => {
        setMac('');
        setPendingMacs([]);
        setErrorMessage(null);
        mobileDispatch({ type: 'SET_CAPTURE_MODE', payload: 'scanner' });
    }, []);

    const handleBarcodeScanned = useCallback(
        (rawValue: string) => {
            if (!state.dialogState.isTagOpen) return;
            playScan();
            const trimmed = rawValue.trim();

            if (!trimmed) {
                setErrorMessage('Please enter MAC.');
                return;
            }

            if (!validateEslMac(trimmed)) {
                setErrorMessage('MAC must be 12 characters.');
                return;
            }
            const finalMac = normalizeEslMac(trimmed);

            if (pendingMacSet.has(finalMac)) {
                setErrorMessage('MAC already added.');
                return;
            }

            setPendingMacs((prev) => [
                ...prev,
                {
                    mac: finalMac,
                },
            ]);
            setErrorMessage(null);
            setMac('');
            macRef.current?.focus();
        },
        [pendingMacSet, state.dialogState.isTagOpen, playScan],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') {
                return;
            }

            event.preventDefault();
            handleBarcodeScanned(mac);
        },
        [handleBarcodeScanned, mac],
    );

    const runScanThroughGate = useCallback(
        async (barcodeWithNewline: string) => {
            const now = performance.now();
            if (!barcodeWithNewline) return;

            const gate = scanGateRef.current;
            if (gate.locked) return;

            // Ignore duplicated scanner output that arrives within the TTL window.
            if (barcodeWithNewline === gate.lastValue && now - gate.lastAt < DUPLICATE_TTL_MS) return;

            gate.locked = true;
            try {
                await handleBarcodeScanned(barcodeWithNewline);
            } finally {
                gate.lastValue = barcodeWithNewline;
                gate.lastAt = performance.now();
                window.setTimeout(() => {
                    gate.locked = false;
                }, COOL_DOWN_MS);
            }
        },
        [handleBarcodeScanned],
    );

    const handleOnBarcodeDetected = useCallback(
        async (barcode: string) => {
            const trimmedBarcode = barcode.replace(/(\r\n|\r|\n)+$/g, '');
            const barcodeWithNewline = `${trimmedBarcode}\n`;
            await runScanThroughGate(barcodeWithNewline);
        },
        [runScanThroughGate],
    );
    const removeMac = useCallback((targetMac: string) => {
        setPendingMacs((prev) => prev.filter((item) => item.mac !== targetMac));
        macRef.current?.focus();
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!pendingMacs.length) {
            return;
        }

        const macList = pendingMacs.map((item) => item.mac);

        dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isTagOpen', isOpen: false } });
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'deviceTableLoading', isLoading: true } });
        dispatch({ type: 'SET_TABLE_STATE', payload: { status: 'loading' } });
        if (!macList.length) return;
        try {
            const {
                data: { isSuccess, message, data },
            } = await registerTagAPI({
                store_id: manageStoreId as number,
                mac_list: macList,
            });

            if (isSuccess) {
                const { confirmed, store_settings } = data || {};
                if (store_settings.asset_list_feature === '1') {
                    await addToWhiteListHandler(macList);
                }
                if (confirmed.length > 0) {
                    setOpenConfirmedTags(true);
                    setConfirmedTags(confirmed);
                }
                setNotification({ message, severity: 'success' });
                void refreshTable();
                return;
            }

            throw new Error(message);
        } catch (error: unknown) {
            dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isTagOpen', isOpen: false } });
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Add Tags'), severity: 'error' });
                return;
            }
            console.error('Error saving settings:', error);
        }
    }, [pendingMacs]);

    const addToWhiteListHandler = async (macList: string[]) => {
        try {
            const {
                data: { isSuccess, message },
            } = await addToWhiteListAPI(manageStoreId as number, macList);
            if (isSuccess) return;
            throw new Error(message || t('Allowlist failed after tag registration'));
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Add WhiteList'), severity: 'error' });
                return;
            }
            console.error('Error saving settings:', error);
        }
    };

    const handleModeChanged = useCallback(() => {
        macRef.current?.focus();
    }, []);

    return (
        <>
            <BaseConfirmDrawer
                title={t('Add TAG')}
                open={state.dialogState.isTagOpen}
                confirmDisabled={pendingMacs.length === 0}
                confirmText={t('Save')}
                cancelText={t('Cancel')}
                onConfirm={handleConfirm}
                onClose={closeDrawer}
                onEnter={() => {
                    macRef.current?.focus();
                }}
                onExited={clearDrawer}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <CaptureManager
                        eslMacInputRef={macRef}
                        onBarcodeDetected={handleOnBarcodeDetected}
                        onModeChanged={handleModeChanged}
                    />

                    <Box>
                        <ScannerInputField
                            ref={macRef}
                            value={mac}
                            setValue={(value) => {
                                setMac(value);

                                if (errorMessage) {
                                    setErrorMessage(null);
                                }
                            }}
                            onClear={() => {
                                setMac('');
                                setErrorMessage(null);
                                macRef.current?.focus();
                            }}
                            onKeyDown={handleKeyDown}
                            onScannerScan={handleBarcodeScanned}
                        />

                        {errorMessage && (
                            <Typography
                                sx={{
                                    mt: 0.75,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#DC2626',
                                }}
                            >
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 0.75,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: '#334155',
                                }}
                            >
                                {t('Add MAC List')}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#64748B',
                                }}
                            >
                                {pendingMacs.length}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                height: 220,
                                overflowY: 'auto',
                                borderRadius: 2,
                                border: '1px solid #E2E8F0',
                                bgcolor: '#FFFFFF',
                            }}
                        >
                            {pendingMacs.length === 0 ? (
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: '#94A3B8',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {t('No MAC Added')}
                                    </Typography>
                                </Box>
                            ) : (
                                pendingMacs.map((item, index) => (
                                    <Box
                                        key={item.mac}
                                        sx={{
                                            px: 1.5,
                                            py: 1.25,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            borderBottom:
                                                index === pendingMacs.length - 1 ? 'none' : '1px solid #F1F5F9',
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    mt: 0.25,
                                                    fontSize: 14,
                                                    fontWeight: 800,
                                                    color: '#0F172A',
                                                    wordBreak: 'break-all',
                                                    letterSpacing: 0.2,
                                                }}
                                            >
                                                {item.mac}
                                            </Typography>
                                        </Box>

                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                removeMac(item.mac);
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Box>
                </Box>
            </BaseConfirmDrawer>
            <BaseDialog
                dialogTitle={t('Registered Tags Detected')}
                open={openConfirmedTags}
                confirmButtonText={t('OK')}
                cancelButtonText=""
                onConfirm={() => setOpenConfirmedTags(false)}
            >
                <Box>
                    <Typography sx={{ mb: 1 }}>{t('These tags are already registered:')}</Typography>
                    <Box sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                        {confirmedTags.slice(0, 10).map((mac) => `- ${mac}\n`)}

                        {confirmedTags.length > 10 && t('...and many more')}
                    </Box>
                </Box>
            </BaseDialog>
        </>
    );
};

export default AddTagDrawer;
